'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui/shadcn/dialog';
import { trackEvent, AnalyticsEvent } from '@/shared/lib/analytics';
import { useOnboardingSlides } from '../hooks/use-onboarding-slides';
import type { OnboardingSlide } from '../api/onboarding';

interface OnboardingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Fires only when the user reaches the last slide and taps the primary button.
  // Used by the autostart wrapper to persist "onboarding seen". A close via the
  // dialog X / backdrop does NOT fire this.
  onComplete?: () => void;
}

export function OnboardingModal({
  open,
  onOpenChange,
  onComplete,
}: OnboardingModalProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [currentPage, setCurrentPage] = useState(0);
  const t = useTranslations('onboarding.slides');
  const tCommon = useTranslations('common');

  const { data: slides, isLoading, error, refetch } = useOnboardingSlides(open);

  const slideCount = slides?.length ?? 0;
  const isLastPage = !!slides && currentPage === slides.length - 1;
  const slide = slides?.[currentPage];

  // Early-funnel analytics (cross-platform contract). `startedRef` guards a
  // single `onboarding_started` per appearance; `terminalRef` enforces exactly
  // one terminal event (completed XOR dismissed) per session.
  const startedRef = useRef(false);
  const terminalRef = useRef(false);

  // Fire `onboarding_started` once, when the carousel first appears with slides
  // loaded (slide_count is only known then). Re-arms on reopen (Help replay).
  useEffect(() => {
    if (open && slideCount > 0 && !startedRef.current) {
      startedRef.current = true;
      terminalRef.current = false;
      trackEvent(AnalyticsEvent.ONBOARDING_STARTED, { slide_count: slideCount });
    }
  }, [open, slideCount]);

  // Reset the per-appearance guard when the modal is fully closed so a later
  // reopen fires `onboarding_started` again.
  useEffect(() => {
    if (!open) startedRef.current = false;
  }, [open]);

  // `onboarding_slide_viewed` for every slide that becomes current, INCLUDING
  // index 0 (fires when `startedRef` flips true, then on each page change).
  useEffect(() => {
    if (open && startedRef.current && slideCount > 0) {
      trackEvent(AnalyticsEvent.ONBOARDING_SLIDE_VIEWED, {
        slide_index: currentPage,
        slide_count: slideCount,
      });
    }
  }, [open, currentPage, slideCount]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentPage(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  function handleNext() {
    if (isLastPage) {
      // Reached the last slide and confirmed → the single "completed" terminal.
      if (!terminalRef.current) {
        terminalRef.current = true;
        trackEvent(AnalyticsEvent.ONBOARDING_COMPLETED, {
          slide_index: currentPage,
          slide_count: slideCount,
        });
      }
      onComplete?.();
      handleClose();
    } else {
      emblaApi?.scrollNext();
    }
  }

  function handleClose() {
    // Any close that isn't a completion is a dismissal (X / backdrop / early
    // close). Fire the "dismissed" terminal before resetting currentPage so we
    // record the slide the user was on. terminalRef makes completed XOR
    // dismissed exclusive.
    if (!terminalRef.current) {
      terminalRef.current = true;
      trackEvent(AnalyticsEvent.ONBOARDING_DISMISSED, {
        slide_index: currentPage,
        slide_count: slideCount,
      });
    }
    onOpenChange(false);
    setCurrentPage(0);
    emblaApi?.scrollTo(0, true);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) handleClose();
        else onOpenChange(value);
      }}
    >
      <DialogContent
        // Phone (<sm): true full-screen, edge-to-edge, no rounding/shadow — the
        // slide owns the whole viewport. We anchor with `inset-0 h-dvh` (NOT the
        // old `top-[50%] translate-y-[-50%]` centering) on purpose: the shared
        // DialogContent is now a bottom sheet on mobile and injects
        // `data-[state=open]:translate-y-0`, which has higher specificity and
        // silently kills any `translate-y-[-50%]` we set — that's what dropped
        // the panel to the lower half of the screen. With `inset-0` we ride the
        // base `translate-y-0` (slide-up from the bottom) correctly. `max-h-none`
        // overrides the base `max-h-[90dvh]` sheet cap so it fills 100dvh.
        // Tablet/desktop (≥sm): centred card, 22rem wide, rounded, shadowed,
        // sitting on the accent overlay (centering comes from the base sm:*).
        className="inset-0 h-dvh max-h-none max-w-none rounded-none shadow-none sm:w-[min(calc(100vw-2rem),22rem)] sm:h-auto sm:max-w-sm sm:rounded-2xl sm:shadow-2xl p-0 gap-0 overflow-hidden bg-background border-none flex flex-col [container-type:inline-size]"
        showCloseButton={false}
        // On phone, the overlay is hidden behind the full-screen card; keep the
        // accent colour for ≥sm where the card floats on it.
        overlayClassName="bg-background sm:bg-[#078D86]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">
          {slide?.attributes.title ?? ''}
        </DialogTitle>

        {isLoading || !slides ? (
          <div className="flex items-center justify-center min-h-[400px] p-8">
            {error ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <p className="text-list-item-subtitle text-muted-foreground">
                  {error.message || 'Failed to load'}
                </p>
                <Button onClick={() => refetch()} variant="outline">
                  {tCommon('tryAgain')}
                </Button>
              </div>
            ) : (
              <div className="size-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            )}
          </div>
        ) : (
          <>
            {/* Swipeable carousel.
                Phone (<sm): a FIXED 3:4 image at the top, capped at 60dvh so
                the text block below always has a predictable amount of room.
                `flex-none` (not the old greedy `flex-1`) is deliberate: the
                image must NOT absorb leftover viewport height, otherwise that
                slack became a gap ABOVE the bottom-pinned text and the title
                "jumped" by the slide's description length. The leftover now
                belongs to the text block (flex-1 there), which top-anchors the
                title right under the image. Tablet/desktop (≥sm): natural 3:4
                aspect inside the auto-height card. */}
            <div
              ref={emblaRef}
              className="overflow-hidden w-full aspect-[3/4] max-h-[60dvh] flex-none sm:max-h-none"
            >
              <div className="flex h-full">
                {slides.map((s, i) => (
                  <SlideImage key={i} slide={s} />
                ))}
              </div>
            </div>

            {/* Content — below carousel. Phone (<sm): the block fills the
                space under the fixed image (flex-1) and TOP-anchors, so the
                badge + title always sit at the same level right under the
                photo, regardless of the slide's description length. The
                description reserves two lines (see min-h below) so the dots
                never shift, and the primary button is pushed to the bottom
                (mt-auto) with a safe-area inset so it's never cramped against
                the home indicator. Tablet/desktop (≥sm): natural height inside
                the auto-height card. */}
            <div className="px-6 pt-5 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-4 flex flex-1 flex-col sm:flex-none">
              {/* Step badge + text */}
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-list-item-value font-semibold text-primary-foreground">
                    {currentPage + 1}
                  </span>
                </div>
                <h3 className="text-section-title font-bold">
                  {slide?.attributes.title}
                </h3>
                {/* Reserve two lines (subtitle: 0.875rem × 1.4 ≈ 1.225rem per
                    line) so a 1-line and a 2-line description occupy the same
                    height — keeps the dots + button from shifting between
                    slides. Longer copy in some locales is allowed to grow. */}
                <p className="text-muted-foreground text-list-item-subtitle min-h-[2.45rem]">
                  {slide?.attributes.description}
                </p>
              </div>

              {/* Dot indicators — clickable to jump to slide */}
              <div className="flex justify-center gap-2 mt-4">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => emblaApi?.scrollTo(i)}
                    aria-label={`Slide ${i + 1} of ${slides.length}`}
                    className="p-1.5 -m-1.5 rounded-full cursor-pointer"
                  >
                    <span
                      className={`block w-2 h-2 rounded-full transition-colors ${
                        i === currentPage
                          ? 'bg-primary'
                          : 'bg-muted-foreground/30'
                      }`}
                    />
                  </button>
                ))}
              </div>

              {/* Button — pinned to the bottom on phone (mt-auto fills the
                  slack under the fixed image), natural spacing on desktop. */}
              <div className="mt-auto pt-4 sm:mt-0">
                <Button onClick={handleNext} className="w-full">
                  {isLastPage ? t('close') : t('next')}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Single slide image with an optional overlay text card (slide 2),
 * showing the API-provided user-goal example with a blinking caret.
 */
function SlideImage({ slide }: { slide: OnboardingSlide }) {
  const { image_url, title, overlay_text } = slide.attributes;

  return (
    <div className="min-w-0 shrink-0 grow-0 basis-full h-full relative overflow-hidden">
      <img
        src={image_url}
        alt={title}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover select-none"
      />

      {overlay_text && (
        <div
          className="absolute flex items-start px-4"
          style={{ top: '18%', left: '10%', right: '10%' }}
        >
          <p className="text-[26px] leading-tight text-black">
            <span>{overlay_text}</span>
            <span className="text-black" aria-hidden="true">|</span>
          </p>
        </div>
      )}
    </div>
  );
}
